import { useCallback, useEffect, useState } from 'react'
import type { AuthChangeEvent, User } from '@supabase/supabase-js'
import { supabase } from './supabase'

const PASSWORD_RECOVERY_PARAM = 'password-recovery'

const hasPasswordRecoveryMarker = () =>
  new URLSearchParams(window.location.search).get(PASSWORD_RECOVERY_PARAM) === '1'

const removePasswordRecoveryMarker = () => {
  const url = new URL(window.location.href)
  url.searchParams.delete(PASSWORD_RECOVERY_PARAM)
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
}

export function useAuthSession() {
  const [user, setUser] = useState<User | null>(null)
  const [ready, setReady] = useState(!supabase)
  const [passwordRecovery, setPasswordRecovery] = useState(hasPasswordRecoveryMarker)

  const completePasswordRecovery = useCallback(() => {
    setPasswordRecovery(false)
    removePasswordRecoveryMarker()
  }, [])

  useEffect(() => {
    if (!supabase) return
    let active = true

    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      setUser(data.session?.user ?? null)
      setReady(true)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session) => {
      setUser(session?.user ?? null)
      setReady(true)
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true)
      if (event === 'SIGNED_OUT') {
        setPasswordRecovery(false)
        removePasswordRecoveryMarker()
      }
    })

    return () => {
      active = false
      subscription.unsubscribe()
    }
  }, [])

  return { user, ready, passwordRecovery, completePasswordRecovery }
}
